/*
 * MDI appointment picker for a Shopify product page.
 *
 * Reserves the slot in Salesforce BEFORE the customer reaches checkout, then goes straight to
 * /checkout rather than the cart. A cart that sits overnight is a van slot lost — and Shopify's own
 * checkout only reserves inventory for about five minutes, so nobody should expect longer here.
 *
 * THREE QUESTIONS, ONE AT A TIME
 *   day -> time -> where. Each answer reveals the next; Add to cart works once both are answered.
 *
 * THE TIME IS RESERVED ON THE CLICK; THE ADDRESS TRAVELS ON THE LINE
 * /hold writes the reservation in a single insert and the site guest user can create but never
 * update, so nothing can be added to that record afterwards. The address goes onto the ORDER
 * instead — which is better anyway: the customer can keep correcting it right up to checkout, and
 * Shop Pay works, because it bypasses the submit handler below but still posts line properties.
 *
 * AND THE RESERVATION IS STILL NOT THE GUARANTEE
 * It stops two people buying the same 09:00 in the ordinary case. The guarantee is Salesforce
 * re-checking the diary when the paid order arrives, because by then the money is gone and the only
 * possible answers are "booked" or "we will rebook you".
 *
 * THREE WAITS, ANSWERED WHERE THE CLICK WAS
 *   day  -> times     a skeleton in the exact shape of the grid that is coming, so nothing jumps
 *   time -> reserved  a spinner inside the slot itself; the others step back but stay readable
 *   buy  -> checkout  the status line takes over, because the page is about to be replaced
 *
 * WHY THE SUBMIT LISTENER IS ON document, IN THE CAPTURE PHASE
 * The theme's <product-form> element binds its own submit handler to this same form. Listeners on
 * the target element fire in registration order regardless of the capture flag, so binding to the
 * form would be a race we might lose. Capturing at document level always runs first, which lets us
 * replace the theme's add-and-open-the-drawer behaviour with a redirect.
 */
(function () {
  var DAYS_AHEAD = 14;
  var SKELETON_CELLS = 12;

  function routeRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  function cartAddUrl() {
    var r = window.routes && window.routes.cart_add_url;
    return r || routeRoot() + 'cart/add.js';
  }

  document.querySelectorAll('[data-ta-slot-picker]').forEach(function (root) {
    if (root.dataset.taReady === '1') return;
    root.dataset.taReady = '1';

    var endpoint = (root.dataset.endpoint || '').replace(/\/+$/, '');
    var service = root.dataset.service;
    var formId = root.dataset.form;
    if (!endpoint || !service) return;

    var daysWrap = root.querySelector('[data-ta-days-wrap]');
    var daysEl = root.querySelector('[data-ta-days]');
    var timesEl = root.querySelector('[data-ta-times]');
    var statusEl = root.querySelector('[data-ta-status]');
    var addrStep = root.querySelector('[data-ta-address-step]');
    var addr = {
      street: root.querySelector('[data-ta-street]'),
      city:   root.querySelector('[data-ta-city]'),
      state:  root.querySelector('[data-ta-state]'),
      zip:    root.querySelector('[data-ta-zip]')
    };
    var labelInput = root.querySelector('[data-ta-prop-label]');
    var idInput = root.querySelector('[data-ta-prop-id]');

    /* The address as it will reach Salesforce: four hidden line properties, rewritten on every
       keystroke so whatever is on screen is what the order carries. */
    var prop = {
        street: root.querySelector('[data-ta-prop-street]'),
        city:   root.querySelector('[data-ta-prop-city]'),
        state:  root.querySelector('[data-ta-prop-state]'),
        postalCode: root.querySelector('[data-ta-prop-zip]')
    };

    var form = formId ? document.getElementById(formId) : null;
    if (!form) form = document.querySelector('form[action*="/cart/add"]');

    var selectedDay = null;
    var announced = false;  // "ready to buy" has already been said
    var holdTimer = null;   // the countdown on the reservation
    var holdEndsAt = 0;
    var clockEl = null;
    var noteEl = null;
    var warned = false;     // the one-minute warning has been spoken
    var busy = false;

    /* Nothing can be bought until a time is held. A scan with no appointment is not a sale — it is
       a refund and an apology. */
    setBuyable(false);

    /* ---------- the visit address ----------
       Asked only once a time is reserved, because a question with no reason yet is the step people
       abandon. It is asked at all because Shopify collects only a BILLING address for a service
       product: pay with a card registered to a practice and the van drives to the practice.

       It is not sent to /hold. The reservation already exists by now and cannot be added to, so
       the address rides on the order line instead and ShopifyOrderParser reads it back. */

    function addressValues() {
      return {
        street: (addr.street.value || '').trim(),
        city: (addr.city.value || '').trim(),
        state: (addr.state.value || '').trim().toUpperCase(),
        postalCode: (addr.zip.value || '').trim()
      };
    }

    function addressComplete() {
      var v = addressValues();
      return !!(v.street && v.city && v.state.length === 2 && /^\d{5}$/.test(v.postalCode));
    }

    /* Buyable means the line carries both answers. Anything less and Add to cart — or Shop Pay —
       would sell a scan with no appointment, or one with nowhere to send the van. */
    function refreshBuyable() {
      var v = addressValues();
      Object.keys(prop).forEach(function (k) { prop[k].value = v[k]; });
      setBuyable(!!idInput.value && addressComplete());
    }

    function markMissing() {
      var v = addressValues();
      addr.street.classList.toggle('is-bad', !v.street);
      addr.city.classList.toggle('is-bad', !v.city);
      addr.state.classList.toggle('is-bad', v.state.length !== 2);
      addr.zip.classList.toggle('is-bad', !/^\d{5}$/.test(v.postalCode));
    }

    Object.keys(addr).forEach(function (k) {
      addr[k].addEventListener('input', function () {
        if (k === 'zip') addr.zip.value = addr.zip.value.replace(/\D/g, '').slice(0, 5);
        if (k === 'state') addr.state.value = addr.state.value.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2);
        addr[k].classList.remove('is-bad');
        refreshBuyable();

        /* Said once, on the keystroke that completes the address — not on every one after it. */
        var ready = !!idInput.value && addressComplete();
        if (noteEl) noteEl.textContent = ' — ' + noteText();
        announced = ready;
      });
    });

    // ---- status -------------------------------------------------------------------------
    // One region, one job. Every path through this file ends in exactly one of these.

    function status(state, text, extra) {
      statusEl.className = 'tsp__status' + (state ? ' is-' + state : '');
      statusEl.innerHTML = '';

      if (state === 'working') {
        statusEl.appendChild(el('span', 'tsp__spinner'));
      } else if (state === 'held') {
        statusEl.appendChild(tick());
      }

      var body = el('span', 'tsp__body');
      body.textContent = text;
      if (extra) {
        var note = el('span', 'tsp__note');
        note.textContent = extra;
        body.appendChild(document.createElement('br'));
        body.appendChild(note);
      }
      statusEl.appendChild(body);
      return statusEl;
    }

    function el(tag, cls) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      return n;
    }

    function tick() {
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'tsp__tick');
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('aria-hidden', 'true');
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M2 8.5l4 4 8-9');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(path);
      return svg;
    }

    /* ---------- the countdown ----------
       A reservation with no visible clock is a promise with no end, and the customer finds out it
       expired by being refused at the till. The deadline comes from the server — holdSeconds, what
       the sweeper will actually act on — so the page and the database release the slot at the same
       moment rather than two that drift apart.

       Seconds remaining, not an expiry timestamp: a browser clock can be minutes out, and an
       expiry converted against a wrong clock counts down to the wrong moment. Elapsed time is the
       one thing a wrong clock still measures correctly.

       The digits are hidden from assistive tech and updated in place. Inside a live region they
       would otherwise be announced once a second, which is unusable; the two moments that matter
       — one minute left, and expiry — are announced instead. */

    function secondsLeft() {
      return Math.max(0, Math.round((holdEndsAt - Date.now()) / 1000));
    }

    function clockText() {
      var left = secondsLeft();
      return Math.floor(left / 60) + ':' + String(left % 60).padStart(2, '0');
    }

    function noteText() {
      return addressComplete()
        ? 'Add to cart to confirm it.'
        : 'Now tell us where the scan should happen.';
    }

    /* Builds the held box once. Only the clock's digits change after this. */
    function showHeld() {
      var box = status('held', labelInput.value + ' is yours');
      var note = el('span', 'tsp__note');

      var lead = document.createElement('span');
      lead.textContent = 'Held for ';
      var clock = el('time', 'tsp__clock');
      clock.setAttribute('aria-hidden', 'true');
      clock.textContent = clockText();
      var rest = document.createElement('span');
      rest.textContent = ' — ' + noteText();

      note.appendChild(lead);
      note.appendChild(clock);
      note.appendChild(rest);
      var body = box.querySelector('.tsp__body');
      body.appendChild(document.createElement('br'));
      body.appendChild(note);

      clockEl = clock;
      noteEl = rest;
    }

    function startHold(seconds) {
      stopHold();
      holdEndsAt = Date.now() + Math.max(0, seconds || 600) * 1000;
      warned = false;
      showHeld();
      holdTimer = setInterval(tickHold, 1000);
    }

    function stopHold() {
      if (holdTimer) { clearInterval(holdTimer); holdTimer = null; }
      clockEl = null;
      noteEl = null;
    }

    function tickHold() {
      var left = secondsLeft();

      if (left <= 0) {
        /* The slot is genuinely free again — the sweeper releases it on this same deadline — so
           the honest thing is to let it go and reload the day, not to leave a dead reservation on
           the page that would be refused at checkout. */
        var lost = selectedDay;
        stopHold();
        clearSelection();
        loadTimes(lost, 'Your hold expired and that time is free again. Pick another.');
        return;
      }

      if (clockEl) clockEl.textContent = clockText();
      statusEl.classList.toggle('is-urgent', left <= 60);

      /* Spoken once, at the point where it changes what a person would do. */
      if (left <= 60 && !warned) {
        warned = true;
        var say = el('span', 'tsp__sr');
        say.textContent = 'Less than a minute left on your reservation.';
        statusEl.appendChild(say);
      }
    }

    function setBuyable(on) {
      if (!form) return;
      form.querySelectorAll('[type="submit"]').forEach(function (b) {
        b.disabled = !on;
        b.setAttribute('aria-disabled', String(!on));
      });
      /* Shop Pay checks out on its own and never reaches the submit handler below, so a disabled
         attribute would not stop it. The stylesheet keeps it off the page; this puts it back once
         the line carries an appointment and an address. */
      form.querySelectorAll('.shopify-payment-button').forEach(function (w) {
        w.classList.toggle('tsp-ready', on);
      });
    }

    /* Drops the chosen time and puts the address question away with it. The typed address is
       kept — it is still true, it just has no appointment to belong to yet. */
    function clearSelection() {
      announced = false;
      stopHold();
      labelInput.value = '';
      idInput.value = '';
      timesEl.querySelectorAll('[aria-pressed="true"]').forEach(function (b) {
        b.setAttribute('aria-pressed', 'false');
      });
      addrStep.hidden = true;
      setBuyable(false);
    }

    // ---- days ---------------------------------------------------------------------------

    function isoDay(d) {
      return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0');
    }

    /* "Wed 9 Sep, 10:00 AM". The API's label is the time alone, which in the cart, the checkout
       and Shopify's own confirmation email reads as an appointment with no day — which is exactly
       how somebody turns up on the wrong morning. */
    function fullLabel(dayKey, timeLabel) {
      return dayName(dayKey, { weekday: 'short', day: 'numeric', month: 'short' })
        + ', ' + timeLabel;
    }

    function dayName(dayKey, opts) {
      var p = dayKey.split('-');
      return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]))
        .toLocaleDateString(undefined, opts);
    }

    function renderDays() {
      var today = new Date();
      var frag = document.createDocumentFragment();
      for (var i = 0; i < DAYS_AHEAD; i++) {
        var d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
        var b = el('button', 'tsp__day');
        b.type = 'button';
        b.dataset.day = isoDay(d);
        b.setAttribute('aria-selected', 'false');
        b.innerHTML = '<span class="tsp__day-dow">' +
          d.toLocaleDateString(undefined, { weekday: 'short' }) +
          '</span><span class="tsp__day-num">' + d.getDate() + '</span>';
        frag.appendChild(b);
      }
      daysEl.appendChild(frag);

      /* Fade the right edge only when the strip really does overflow, so the last day is never
         dimmed for no reason. */
      var check = function () {
        daysWrap.classList.toggle('is-scrollable',
          daysEl.scrollWidth > daysEl.clientWidth + 4);
      };
      check();
      window.addEventListener('resize', check);
    }

    // ---- times --------------------------------------------------------------------------

    function skeleton() {
      var wrap = el('div', 'tsp__slots');
      wrap.setAttribute('aria-hidden', 'true');
      for (var i = 0; i < SKELETON_CELLS; i++) wrap.appendChild(el('div', 'tsp__skel-cell'));
      timesEl.innerHTML = '';
      timesEl.appendChild(wrap);
      timesEl.setAttribute('aria-busy', 'true');
    }

    /* keepMessage: a reason the day is being reloaded — "that time has just gone" — which must
       survive the reload. Otherwise the explanation is replaced by a slot count a moment later and
       the customer never learns why their choice vanished. */
    function loadTimes(dayKey, keepMessage) {
      skeleton();
      if (keepMessage) {
        status('error', keepMessage);
      } else {
        status('working', 'Finding times for ' + dayName(dayKey, { weekday: 'long', day: 'numeric', month: 'long' }) + '…');
      }

      fetch(endpoint + '/availability?date=' + encodeURIComponent(dayKey) +
            '&service=' + encodeURIComponent(service), { method: 'GET' })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (dayKey !== selectedDay) return;   // a later click won
          timesEl.setAttribute('aria-busy', 'false');

          var slots = (data && data.slots) || [];
          if (!slots.length) {
            timesEl.innerHTML = '';
            status('', 'No times left on '
              + dayName(dayKey, { weekday: 'long', day: 'numeric', month: 'long' })
              + '. Try another day.');
            return;
          }

          var wrap = el('div', 'tsp__slots');
          slots.forEach(function (s) {
            var b = el('button', 'tsp__slot');
            b.type = 'button';
            b.setAttribute('aria-pressed', 'false');
            b.dataset.start = s.startTime;
            b.dataset.label = s.label;
            b.textContent = s.label;
            wrap.appendChild(b);
          });
          timesEl.innerHTML = '';
          timesEl.appendChild(wrap);
          if (!keepMessage) {
            status('', slots.length + (slots.length === 1 ? ' time' : ' times') + ' available. Pick one.');
          }
        })
        .catch(function () {
          if (dayKey !== selectedDay) return;
          timesEl.innerHTML = '';
          timesEl.setAttribute('aria-busy', 'false');
          /* A dead end with no way out is worse than the failure itself. */
          var s = status('error', 'Could not load times.');
          var retry = el('button', 'tsp__retry');
          retry.type = 'button';
          retry.textContent = 'Try again';
          retry.addEventListener('click', function () { loadTimes(dayKey); });
          s.appendChild(retry);
        });
    }

    // ---- reserving ----------------------------------------------------------------------

    /* Reserves the slot as soon as it is chosen, so that Shop Pay — which cannot be intercepted —
       never has an unbooked line to check out. No address: it does not exist yet, and could not be
       added to this record afterwards even if it did. */
    function hold(startTime, label, button) {
      if (busy) return;
      busy = true;

      var slots = button.parentElement;
      slots.classList.add('is-busy');
      button.classList.add('is-working');
      status('working', 'Reserving ' + fullLabel(selectedDay, label) + '…');

      fetch(endpoint + '/hold', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        /* No name or email either. Shopify asks for those at checkout, and its answer is the one
           that matters because it is where the receipt goes. */
        body: JSON.stringify({ service: service, startTime: startTime, source: 'shopify' })
      })
        .then(function (r) {
          return r.json().then(function (b) { return { ok: r.ok, body: b }; });
        })
        .then(function (res) {
          busy = false;
          slots.classList.remove('is-busy');
          button.classList.remove('is-working');

          if (!res.ok || !res.body || res.body.success !== true) {
            /* A 409 means somebody took it between the list loading and this click. Say so plainly
               and reload the day, rather than letting them carry a slot that is gone. */
            button.setAttribute('aria-pressed', 'false');
            var lost = selectedDay;
            clearSelection();
            loadTimes(lost, (res.body && res.body.message) || 'That time has just gone.');
            return;
          }

          labelInput.value = fullLabel(selectedDay, label);
          idInput.value = res.body.bookingId;

          /* Now there is an appointment for the address to belong to. */
          var firstTime = addrStep.hidden;
          addrStep.hidden = false;
          refreshBuyable();

          announced = addressComplete();
          startHold(res.body.holdSeconds);
          if (!announced && firstTime) addr.street.focus();
        })
        .catch(function () {
          busy = false;
          slots.classList.remove('is-busy');
          button.classList.remove('is-working');
          button.setAttribute('aria-pressed', 'false');
          clearSelection();
          status('error', 'Could not reserve that time. Try again.');
        });
    }

    // ---- events -------------------------------------------------------------------------

    daysEl.addEventListener('click', function (e) {
      var button = e.target.closest('[data-day]');
      if (!button || busy) return;
      selectedDay = button.dataset.day;
      daysEl.querySelectorAll('[data-day]').forEach(function (b) {
        b.setAttribute('aria-selected', String(b === button));
      });
      /* Changing day drops the chosen time. Leaving it would let somebody buy Tuesday while the
         page says Thursday. */
      clearSelection();
      loadTimes(selectedDay);
    });

    timesEl.addEventListener('click', function (e) {
      var button = e.target.closest('[data-start]');
      if (!button || busy) return;
      timesEl.querySelectorAll('[data-start]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b === button));
      });

      hold(button.dataset.start, button.dataset.label, button);
    });

    // ---- add to cart --------------------------------------------------------------------

    /* Removes every cart line carrying a _booking_id. Resolves either way: failing to tidy the
       cart must not stop somebody buying, and a duplicate line is recoverable where a broken Add
       to cart is not. */
    function clearExistingAppointmentLines() {
      return fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var doomed = (cart.items || []).filter(function (item) {
            return item.properties && item.properties._booking_id;
          });
          if (!doomed.length) return null;

          /* One at a time and by KEY, not by line number: removing a line renumbers the ones
             after it, so an index-based loop deletes the wrong thing on the second pass. */
          return doomed.reduce(function (chain, item) {
            return chain.then(function () {
              return fetch(routeRoot() + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: item.key, quantity: 0 })
              });
            });
          }, Promise.resolve());
        })
        .catch(function () { return null; });
    }

    if (form) {
      document.addEventListener('submit', function (e) {
        if (e.target !== form) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (busy) return;

        if (!idInput.value) {
          status('error', 'Pick an appointment time first.');
          return;
        }
        if (!addressComplete()) {
          addrStep.hidden = false;
          markMissing();
          status('error', 'We need the full address before we can book the van.');
          addr.street.focus();
          return;
        }

        /* The page is about to be replaced, so the wait is answered here rather than on a button
           that is about to disappear. A reservation, a cart write and a navigation is long enough
           that silence reads as a broken button. */
        busy = true;
        setBuyable(false);
        status('working', 'Taking you to checkout…');

        clearExistingAppointmentLines()
          .then(function () {
            return fetch(cartAddUrl(), { method: 'POST', body: new FormData(form) });
          })
          .then(function (res) {
            if (!res.ok) throw new Error('add failed');
            window.location = routeRoot() + 'checkout';
          })
          .catch(function () {
            busy = false;
            setBuyable(true);
            status('error', 'Could not start checkout. Try again.');
          });
      }, true);
    }

    renderDays();
    status('', 'Pick a day to see available times.');
  });
})();
